import { useEffect } from 'react';
import { Analytics } from '@vercel/analytics/react';
import { Landing } from './screens/Landing';
import { Today } from './screens/Today';
import { Explore } from './screens/Explore';
import { Profile } from './screens/Profile';
import { Player } from './screens/Player';
import { TabBar, type TabId } from './ui/parts';
import { useLocation } from './utils/router';
import { getPractice } from './lib/catalog';
import { getRoutine } from './lib/routines';
import { endRun } from './lib/sessionStore';
import { isReturning } from './lib/storage';

const TAB_PATH: Record<TabId, string> = { today: '/today', explore: '/explore', profile: '/profile' };

function App() {
  const { path, navigate } = useLocation();

  const playMatch = path.match(/^\/play\/([^/]+)\/?$/);
  const sessionMatch = path.match(/^\/session\/([^/]+)\/?$/);
  const inPlayer = Boolean(playMatch || sessionMatch);
  const returningAtRoot = path === '/' && isReturning();
  const legacy = path.startsWith('/practice');

  // Returning devices skip the welcome page; old /practice URLs land on Today.
  useEffect(() => {
    if (returningAtRoot || legacy) navigate('/today', { replace: true });
  }, [returningAtRoot, legacy, navigate]);

  // Leaving the player by any route (browser Back included) stops the session.
  useEffect(() => {
    if (!inPlayer) endRun();
  }, [inPlayer]);

  const play = (id: string) => navigate(`/play/${id}`);
  const playRoutine = (id: string) => navigate(`/session/${id}`);
  const home = () => navigate('/today', { replace: true });

  let screen;
  let tab: TabId | null = null;

  if (sessionMatch) {
    const routine = getRoutine(sessionMatch[1]);
    screen = routine ? <Player routine={routine} onExit={home} /> : <Today onPlayRoutine={playRoutine} onExplore={() => navigate('/explore')} />;
    if (!routine) tab = 'today';
  } else if (playMatch) {
    const practice = getPractice(playMatch[1]);
    screen = practice ? <Player practice={practice} onExit={home} /> : <Today onPlayRoutine={playRoutine} onExplore={() => navigate('/explore')} />;
    if (!practice) tab = 'today';
  } else if (path === '/explore') {
    screen = <Explore onPlay={play} />;
    tab = 'explore';
  } else if (path === '/profile') {
    screen = <Profile onWelcome={() => navigate('/welcome')} />;
    tab = 'profile';
  } else if (path === '/welcome' || (path === '/' && !returningAtRoot)) {
    screen = <Landing onStart={() => navigate('/today')} onExplore={() => navigate('/explore')} />;
  } else {
    screen = <Today onPlayRoutine={playRoutine} onExplore={() => navigate('/explore')} />;
    tab = 'today';
  }

  return (
    <>
      <Analytics />
      {screen}
      {tab && <TabBar active={tab} onSelect={t => navigate(TAB_PATH[t])} />}
    </>
  );
}

export default App;
